/**
 * React bindings for the non-React world.
 *
 * The rule these enforce: React never sees a market data tick. It sees a
 * throttled "something changed" pulse, and canvases are driven entirely
 * outside the component tree by the shared frame loop.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { acquireSurface, frameLoop, type CanvasSurface, type FrameStats } from '../lib/frame';
import { store } from '../data/store';
import { pool, type PoolStatus } from '../data/socket';

/** Re-render at the store's throttled UI rate (~8Hz). For text chrome only. */
export function useStoreTick(): number {
  return useSyncExternalStore(
    useCallback((cb: () => void) => store.tick.on(cb), []),
    () => store.version,
    () => store.version,
  );
}

/** Re-render only when symbols / active / interval change. */
export function useStructure(): number {
  return useSyncExternalStore(
    useCallback((cb: () => void) => store.structure.on(cb), []),
    () => store.structureVersion,
    () => store.structureVersion,
  );
}

export function usePoolStatus(): PoolStatus {
  const [status, setStatus] = useState<PoolStatus>(() => pool.snapshot());
  useEffect(() => {
    setStatus(pool.snapshot());
    return pool.status.on(setStatus);
  }, []);
  return status;
}

/** Frame statistics, sampled twice a second. */
export function useFrameStats(): FrameStats {
  const [stats, setStats] = useState<FrameStats>(() => frameLoop.stats());
  useEffect(() => {
    const id = window.setInterval(() => setStats(frameLoop.stats()), 500);
    return () => clearInterval(id);
  }, []);
  return stats;
}

/** Always-current ref to a callback, so effects need not depend on it. */
export function useLatest<T>(value: T) {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

export interface Size {
  width: number;
  height: number;
}

/** Observed CSS size of an element. Updates are batched by the browser. */
export function useElementSize<T extends HTMLElement>(): [React.RefObject<T | null>, Size] {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (!e) return;
      // borderBoxSize is more reliable than contentRect inside flex layouts.
      const box = e.borderBoxSize?.[0];
      const w = box ? box.inlineSize : e.contentRect.width;
      const h = box ? box.blockSize : e.contentRect.height;
      setSize((prev) =>
        Math.abs(prev.width - w) < 0.5 && Math.abs(prev.height - h) < 0.5
          ? prev
          : { width: w, height: h },
      );
    });
    ro.observe(el);
    const r = el.getBoundingClientRect();
    setSize({ width: r.width, height: r.height });
    return () => ro.disconnect();
  }, []);

  return [ref, size];
}

export interface CanvasPanelOptions {
  /** Draw order within the shared loop. Lower runs first. */
  priority?: number;
  /**
   * Return false to skip this frame. Keep it cheap — it runs every frame.
   * Omitting it means the panel repaints unconditionally at 60fps.
   */
  dirty?: () => boolean;
}

/**
 * Bind a canvas to the shared frame loop.
 *
 * The canvas is sized to its own CSS box at (capped) device pixel ratio on
 * every frame where the box changed, which means resizing the grid needs no
 * React involvement at all — the next frame simply measures a different box.
 */
export function useCanvasPanel(
  draw: (surface: CanvasSurface, now: number, dt: number) => void,
  options: CanvasPanelOptions = {},
): React.RefObject<HTMLCanvasElement | null> {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawRef = useLatest(draw);
  const dirtyRef = useLatest(options.dirty);
  const priority = options.priority ?? 0;

  useEffect(() => {
    const task = {
      priority,
      dirty: () => {
        const el = canvasRef.current;
        if (!el) return false;
        const d = dirtyRef.current;
        return d ? d() : true;
      },
      draw: (now: number, dt: number) => {
        const el = canvasRef.current;
        if (!el) return;
        // clientWidth/Height are cheap reads here: we never write layout in
        // the loop, so there is nothing to force a synchronous reflow.
        const surface = acquireSurface(el, el.clientWidth, el.clientHeight);
        if (!surface) return;
        drawRef.current(surface, now, dt);
      },
    };
    return frameLoop.add(task);
  }, [priority, drawRef, dirtyRef]);

  return canvasRef;
}

/**
 * A value that only changes when `keyOf` changes, evaluated on the store tick.
 * Lets a text component re-render at 8Hz but bail out when its own numbers
 * did not actually move.
 */
export function useDerived<T>(compute: () => T, isEqual: (a: T, b: T) => boolean): T {
  useStoreTick();
  const ref = useRef<T>(undefined as T);
  const next = compute();
  if (ref.current === undefined || !isEqual(ref.current, next)) {
    ref.current = next;
  }
  return ref.current;
}

/** localStorage-backed state that survives a reload. */
export function usePersisted<T>(key: string, initial: T): [T, (v: T | ((p: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });

  const set = useCallback(
    (v: T | ((p: T) => T)) => {
      setValue((prev) => {
        const next = typeof v === 'function' ? (v as (p: T) => T)(prev) : v;
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          /* quota / private mode */
        }
        return next;
      });
    },
    [key],
  );

  return [value, set];
}

/** Escape-key handler for popovers and modals. */
export function useEscape(active: boolean, onEscape: () => void): void {
  const cb = useLatest(onEscape);
  useEffect(() => {
    if (!active) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        cb.current();
      }
    };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [active, cb]);
}

/** Dismiss on any pointer press outside the element. */
export function useDismiss<T extends HTMLElement>(
  active: boolean,
  onDismiss: () => void,
): React.RefObject<T | null> {
  const ref = useRef<T | null>(null);
  const cb = useLatest(onDismiss);
  useEffect(() => {
    if (!active) return;
    const h = (e: PointerEvent) => {
      const el = ref.current;
      if (el && !el.contains(e.target as Node)) cb.current();
    };
    // Defer so the click that opened the popover does not immediately close it.
    const id = window.setTimeout(() => window.addEventListener('pointerdown', h), 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener('pointerdown', h);
    };
  }, [active, cb]);
  return ref;
}
